import { featureFlagsAtom } from '@polaris/core/stores'
import { formatCoin } from '@polaris/core/utils'
import { useFieldActions, useFieldState, useForm } from 'form-atoms'
import { useAtomValue } from 'jotai'
import { type HTMLAttributes, useCallback, useMemo } from 'react'
import { Banner } from '@/src/components/ui/banner'
import { useTokenFieldActions } from '@/src/stores/swap/actions'
import {
  feeAtom,
  insufficientBalanceErrorAtom,
  insufficientBalanceForFeesErrorAtom,
  insufficientBalanceForFeesWarningAtom,
  swapFormAtom,
} from '@/src/stores/swap/atoms'
import { cn } from '@/src/utils/styles'

export const CTA = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  const { setValue } = useTokenFieldActions({ direction: 'to' })
  const insufficientBalanceError = useAtomValue(insufficientBalanceErrorAtom)

  const insufficientBalanceForFeesError = useAtomValue(
    insufficientBalanceForFeesErrorAtom,
  )
  const isInsufficientBalanceForFeesError =
    insufficientBalanceForFeesError && !insufficientBalanceError

  const insufficientBalanceForFeesWarning = useAtomValue(
    insufficientBalanceForFeesWarningAtom,
  )

  const { fieldAtoms } = useForm(swapFormAtom)
  const fromState = useFieldState(fieldAtoms.from)
  const fromAmountActions = useFieldActions(fieldAtoms.fromAmount)
  const fee = useAtomValue(feeAtom)
  const { trading } = useAtomValue(featureFlagsAtom)

  const alertTile = useMemo(() => {
    if (!trading) return 'Trading temporarily unavailable'
    if (isInsufficientBalanceForFeesError)
      return `Insufficient ${fee?.ticker} for fees`
    if (insufficientBalanceForFeesWarning)
      return 'Intermediary route step balances may be insufficient'
  }, [
    isInsufficientBalanceForFeesError,
    fee?.ticker,
    trading,
    insufficientBalanceForFeesWarning,
  ])

  const description = useMemo(() => {
    if (!trading)
      return "We're in beta and taking a short pause for maintenance. Thanks for bearing with us — the adventure continues soon!"
    if (isInsufficientBalanceForFeesError && insufficientBalanceForFeesError)
      return `You need an additional ${formatCoin({
        amount: insufficientBalanceForFeesError.requiredAmount,
        denom: fee?.ticker,
      })} to cover fees for this trade.`
    if (insufficientBalanceForFeesWarning)
      return insufficientBalanceForFeesWarning
  }, [
    isInsufficientBalanceForFeesError,
    insufficientBalanceForFeesError,
    fee?.ticker,
    trading,
    insufficientBalanceForFeesWarning,
  ])

  const ctaLabel = useMemo(() => {
    if (isInsufficientBalanceForFeesError) return `Get more ${fee?.ticker}`

    return ''
  }, [isInsufficientBalanceForFeesError, fee?.ticker])

  const ctaAction = useCallback(() => {
    if (isInsufficientBalanceForFeesError && fee && fromState.value?.chain) {
      setValue({
        chain_id: fromState.value?.chain,
        denom: fee?.denom,
      })
      fromAmountActions.setValue('')
    }
  }, [
    isInsufficientBalanceForFeesError,
    fee,
    fromState.value?.chain,
    setValue,
    fromAmountActions,
  ])

  if (
    !isInsufficientBalanceForFeesError &&
    !insufficientBalanceForFeesWarning &&
    trading
  ) {
    return null
  }

  return (
    <Banner
      alertTitle={alertTile}
      description={description}
      variant='warning'
      placement='inline'
      primaryAction={
        insufficientBalanceForFeesError
          ? {
              label: ctaLabel,
              onClick: ctaAction,
            }
          : undefined
      }
      hideIcon
      className={cn('mb-4', className)}
      {...props}
    />
  )
}
